import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  TextField,
  Box,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { newNode } from '../../../utils/ideaTool';
import { sendNewNodeMessage } from '../../../utils/socketTool';

/**
 * QuestionnaireNodeCreator - 建立問卷節點的對話框
 *
 * 問卷節點以 tags='questionnaire' 儲存，
 * content 為 JSON 字串：{ description, questions: [{id, text}] }
 */
export const QuestionnaireNodeCreator = ({
  open,
  onClose,
  ws,
  groupId,
  activityId,
}) => {
  const userName = localStorage.getItem('userName') || '匿名用戶';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // 使用計數器追蹤問題 ID（避免刪除後 ID 衝突）
  const nextIdRef = React.useRef(2);
  const [questions, setQuestions] = useState([{ id: 1, text: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAddQuestion = () => {
    const newId = nextIdRef.current++;
    setQuestions((prev) => [...prev, { id: newId, text: '' }]);
  };

  const handleRemoveQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleQuestionChange = (idx, value) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === idx ? { ...q, text: value } : q))
    );
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setQuestions([{ id: 1, text: '' }]);
    nextIdRef.current = 2;
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('請填寫問卷標題');
      return;
    }
    const validQuestions = questions.filter((q) => q.text.trim());
    if (validQuestions.length === 0) {
      setError('請至少填寫一個問題');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const resolvedActivityId =
        activityId || sessionStorage.getItem('activityId');
      const resolvedGroupId = groupId || sessionStorage.getItem('groupId');

      const content = JSON.stringify({
        description: description.trim(),
        questions: validQuestions.map((q, idx) => ({
          id: idx + 1,
          text: q.text.trim(),
        })),
      });

      const ideaData = {
        title: title.trim(),
        content,
        type: 'questionnaire',
        tags: 'questionnaire',
        author: userName,
        groupId: resolvedGroupId,
      };

      await newNode(ideaData, resolvedActivityId, ws);
      handleClose();
    } catch (err) {
      console.error('建立問卷節點失敗:', err);
      setError('建立問卷節點失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    title.trim() && questions.some((q) => q.text.trim()) && !loading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
    >
      <DialogTitle>建立問卷節點 📋</DialogTitle>
      <Divider variant="middle" />
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
          <TextField
            required
            autoFocus
            label="問卷標題"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
          <FormHelperText>為這份問卷下一個清楚的標題</FormHelperText>
        </FormControl>

        <FormControl variant="standard" fullWidth sx={{ mb: 3 }}>
          <TextField
            label="問卷描述（選填）"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <FormHelperText>說明這份問卷的目的或背景</FormHelperText>
        </FormControl>

        <Typography variant="subtitle2" gutterBottom>
          問題列表
        </Typography>

        {questions.map((q, idx) => (
          <Box
            key={idx}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ mb: 1.5 }}
          >
            <Typography
              variant="body2"
              sx={{ minWidth: 28, color: 'text.secondary' }}
            >
              {idx + 1}.
            </Typography>
            <TextField
              value={q.text}
              onChange={(e) => handleQuestionChange(idx, e.target.value)}
              placeholder={`問題 ${idx + 1}`}
              variant="outlined"
              size="small"
              fullWidth
            />
            <IconButton
              onClick={() => handleRemoveQuestion(idx)}
              disabled={questions.length <= 1}
              size="small"
              color="error"
              aria-label="刪除問題"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddQuestion}
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
        >
          新增問題
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          取消
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          loadingPosition="start"
          variant="contained"
          disabled={!canSubmit}
        >
          建立問卷
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionnaireNodeCreator;
